// src/client/hooks/usePwaInstall.ts
// Captures the browser's `beforeinstallprompt` event and exposes a hook so
// any component can trigger the native PWA install dialog on demand.
//
// Why module-level capture:
//   Chrome fires `beforeinstallprompt` shortly after page load — often before
//   React's first render. By registering the listener at module-evaluation time
//   we ensure no call to usePwaInstall() ever misses the event, regardless of
//   when the hook is first mounted.

import { useState, useEffect } from 'react';

/**
 * `BeforeInstallPromptEvent` is not in TypeScript's lib.dom.d.ts because the
 * spec is still evolving. Declare it locally.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Module-level state — shared across all hook instances in the same bundle.
// ---------------------------------------------------------------------------
let _deferredPrompt: BeforeInstallPromptEvent | null = null;
const _subscribers = new Set<() => void>();

function _notify(): void {
  _subscribers.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  // e.preventDefault() suppresses Chrome's mini-infobar / bottom sheet so we
  // can show our own install button at a better moment.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e as BeforeInstallPromptEvent;
    _notify();
  });

  // Clear once the app is actually installed (e.g. via the OS / browser UI).
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    _notify();
  });
}

// ---------------------------------------------------------------------------

export interface PwaInstallState {
  /** True when the browser has signalled the app can be installed. */
  canInstall: boolean;
  /** True when already running in standalone mode (PWA is installed). */
  isInstalled: boolean;
  /** Show the native install dialog. Must be called from a user-gesture handler. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

export function usePwaInstall(): PwaInstallState {
  const [canInstall, setCanInstall] = useState<boolean>(() => _deferredPrompt !== null);

  // matchMedia is not defined in SSR / Node; guard defensively.
  const isInstalled =
    typeof window !== 'undefined'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;

  useEffect(() => {
    const refresh = () => setCanInstall(_deferredPrompt !== null);
    _subscribers.add(refresh);
    // Sync — in case the event fired between module evaluation and first render.
    refresh();
    return () => {
      _subscribers.delete(refresh);
    };
  }, []);

  const install = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!_deferredPrompt) return 'unavailable';
    const prompt = _deferredPrompt;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    _deferredPrompt = null;
    setCanInstall(false);
    return outcome;
  };

  return { canInstall, isInstalled, install };
}

/**
 * @internal Only for use in Jest tests.
 * Resets the module-level install capture state between test cases.
 */
export function _testReset(): void {
  _deferredPrompt = null;
  _subscribers.clear();
}
